import { useEffect, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';

const { t } = i18n;

interface MatrixResponse {
  capabilities: Record<string, string[]>;
}

export function RbacMatrixPage() {
  const [matrix, setMatrix] = useState<Record<string, string[]> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<MatrixResponse>('/auth/rbac/matrix')
      .then((res) => setMatrix(res.capabilities))
      .catch((err: unknown) => setError(String(err)));
  }, []);

  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!matrix) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  const allRoles = Array.from(
    new Set(Object.values(matrix).flat()),
  ).sort();
  const capabilities = Object.keys(matrix).sort();

  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-xl font-semibold">{t('admin.rbac.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('admin.rbac.subtitle')}</p>
      </header>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="px-3 py-2 text-left font-medium">
                {t('admin.rbac.capability')}
              </th>
              {allRoles.map((r) => (
                <th key={r} className="px-3 py-2 text-center font-medium">
                  <span title={t(`role.${r}`)}>{r}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {capabilities.map((cap) => (
              <tr key={cap} className="border-t border-border">
                <td className="px-3 py-2 font-mono text-xs">{cap}</td>
                {allRoles.map((role) => (
                  <td key={role} className="px-3 py-2 text-center">
                    {matrix[cap]?.includes(role) ? (
                      <span className="text-emerald-600">✓</span>
                    ) : (
                      <span className="text-muted-foreground/40">·</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">{t('admin.rbac.footnote')}</p>
    </section>
  );
}
